import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { track } from "../lib/analytics";
import AppLayout from "../components/app/AppLayout";
import WorkshopVsCatalogRadar from "../components/machines/WorkshopVsCatalogRadar";
import QuickMachineSave from "../components/machines/QuickMachineSave";
import CatalogToolbar from "../components/machines/CatalogToolbar";
import { Wrench, Plus, Trash2, Edit3, FileText, Upload, X, Save, BookOpen, Settings2, PackagePlus, Check, ChevronDown, ChevronUp, BarChart3, Loader2 } from "lucide-react";

const EMPTY = {
  name: "", brand: "", model: "", category_id: "tornio_cnc",
  hourly_rate: 55, precision_um: 10, axes: 3, max_workspace_mm: "", notes: "",
  // custom workshop specs
  spindle_max_rpm: "", spindle_power_kw: "", travel_x_mm: "", travel_y_mm: "", travel_z_mm: "",
  controller: "", tool_changer_capacity: "", max_workpiece_kg: "", coolant: "",
  has_probe: false, has_pallet_changer: false, location_in_shop: "",
  year: "", serial_number: "", spindle_taper: "",
  workable_materials: "", certifications: "", technical_notes: "",
};

export default function MyMachinesPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState("custom");
  const [userMachines, setUserMachines] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [brandFilter, setBrandFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [search, setSearch] = useState("");
  const [showCustomSpecs, setShowCustomSpecs] = useState(false);
  const [formError, setFormError] = useState("");
  // Upload progress per machine ({ [machineId]: 0-100 }) and last upload error.
  const [uploads, setUploads] = useState({});
  const [docErrors, setDocErrors] = useState({});
  // Guards against double submits (fast double click / Enter + click).
  const savingRef = useRef(false);
  // Specs the backend estimated from the catalog: saved only if the user edits them.
  const originalRef = useRef(null);

  const load = useCallback(async () => {
    const [um, cat, cats] = await Promise.all([
      api.get("/user-machines"),
      api.get("/catalog/machine-models"),
      api.get("/catalog/machines"),
    ]);
    setUserMachines(um.data || []);
    setCatalog(cat.data || []);
    setCategories(cats.data || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const startNew = () => { setForm(EMPTY); setEditing("new"); setShowCustomSpecs(false); setFormError(""); originalRef.current = null; };
  const startEdit = (m) => {
    originalRef.current = m;
    setFormError("");
    setForm({ ...EMPTY, ...m });
    setEditing(m.id);
    // auto-expand custom specs if any value is filled
    const hasCustom = ["spindle_max_rpm","spindle_power_kw","travel_x_mm","travel_y_mm","travel_z_mm",
      "controller","tool_changer_capacity","max_workpiece_kg","workable_materials","certifications","technical_notes"]
      .some((k) => m[k]);
    setShowCustomSpecs(hasCustom);
  };
  const cancel = () => { setForm(EMPTY); setEditing(null); setShowCustomSpecs(false); setFormError(""); };

  const save = async (e) => {
    e.preventDefault();
    if (savingRef.current) return;
    savingRef.current = true;
    setBusy(true);
    setFormError("");
    try {
      const payload = {
        ...form,
        hourly_rate: parseFloat(form.hourly_rate) || 0,
        precision_um: form.precision_um !== "" ? parseFloat(form.precision_um) : null,
        axes: form.axes !== "" ? parseInt(form.axes) : null,
        spindle_max_rpm: form.spindle_max_rpm !== "" ? parseInt(form.spindle_max_rpm) : null,
        spindle_power_kw: form.spindle_power_kw !== "" ? parseFloat(form.spindle_power_kw) : null,
        travel_x_mm: form.travel_x_mm !== "" ? parseFloat(form.travel_x_mm) : null,
        travel_y_mm: form.travel_y_mm !== "" ? parseFloat(form.travel_y_mm) : null,
        travel_z_mm: form.travel_z_mm !== "" ? parseFloat(form.travel_z_mm) : null,
        tool_changer_capacity: form.tool_changer_capacity !== "" ? parseInt(form.tool_changer_capacity) : null,
        max_workpiece_kg: form.max_workpiece_kg !== "" ? parseFloat(form.max_workpiece_kg) : null,
        year: form.year !== "" ? parseInt(form.year) : null,
      };
      const orig = originalRef.current;
      (orig?._specs_from_catalog || []).forEach((k) => {
        if (String(form[k] ?? "") === String(orig[k] ?? "")) payload[k] = null;
      });
      if (editing === "new") {
        await api.post("/user-machines", payload);
        track("machine_added", {
          category_id: payload.category_id || null,
          hourly_rate: payload.hourly_rate || 0,
          axes: payload.axes || null,
          source: "custom",
        });
        toast.success(t("machines.savedNew", { name: payload.name }));
      } else {
        await api.put(`/user-machines/${editing}`, payload);
        track("machine_updated", { category_id: payload.category_id || null, source: form.from_catalog_id ? "catalog" : "custom" });
        toast.success(t("machines.savedUpdate", { name: payload.name }));
      }
      cancel(); await load();
    } catch (e2) {
      const msg = formatApiError(e2.response?.data?.detail) || t("machines.saveError");
      setFormError(e2.response ? msg : t("machines.saveError"));
      toast.error(e2.response ? msg : t("machines.saveError"));
    }
    savingRef.current = false;
    setBusy(false);
  };

  const quickSave = async (cat, rate) => {
    try {
      await api.post("/user-machines", { name: cat.name, category_id: cat.id, hourly_rate: rate });
      track("machine_added", { category_id: cat.id, hourly_rate: rate, source: "quick" });
      toast.success(t("machines.quickSaved", { name: cat.name }));
      await load();
      return null;
    } catch (e) {
      return (e.response && formatApiError(e.response.data?.detail)) || t("machines.saveError");
    }
  };

  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleteError, setDeleteError] = useState("");
  const [openRadar, setOpenRadar] = useState({});

  // Build a quick lookup of "optimal catalog machine" per category — the
  // catalog (`/catalog/machines`) already returns one generic entry per
  // category_id with enriched specs (spindle_kw, max_torque_nm, max_rpm,
  // roughing_capacity_cm3_min). So we just index by id.
  const optimalByCategory = useMemo(() => {
    const map = new Map();
    for (const c of categories || []) {
      if (c?.id) map.set(c.id, c);
    }
    return map;
  }, [categories]);

  const toggleRadar = (id) => setOpenRadar((s) => ({ ...s, [id]: !s[id] }));

  const remove = async (id) => {
    setDeleteError("");
    try {
      await api.delete(`/user-machines/${id}`);
      setConfirmDelete(null);
      await load();
    } catch (e) {
      setDeleteError(e.response?.data?.detail || t("common.error"));
    }
  };

  const uploadDoc = async (mid, file) => {
    if (!file || uploads[mid] != null) return;
    if (file.size > 10 * 1024 * 1024) {
      setDocErrors((s) => ({ ...s, [mid]: t("machines.pdfTooBig") }));
      return;
    }
    setDocErrors((s) => ({ ...s, [mid]: "" }));
    setUploads((s) => ({ ...s, [mid]: 0 }));
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post(`/user-machines/${mid}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (ev) => ev.total && setUploads((s) => ({ ...s, [mid]: Math.round((ev.loaded / ev.total) * 100) })),
      });
      track("machine_document_uploaded", { size_kb: Math.round(file.size / 1024) });
      toast.success(t("machines.pdfUploaded", { name: file.name }));
      await load();
    } catch (e) {
      const msg = (e.response && formatApiError(e.response.data?.detail)) || t("machines.saveError");
      setDocErrors((s) => ({ ...s, [mid]: msg }));
      toast.error(msg);
    }
    setUploads((s) => { const next = { ...s }; delete next[mid]; return next; });
  };

  const removeDoc = async (mid, did) => {
    try {
      await api.delete(`/user-machines/${mid}/documents/${did}`);
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || t("common.error"));
    }
  };

  const addFromCatalog = async (modelId) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/user-machines/from-catalog/${modelId}`);
      track("machine_added", { category_id: data?.category_id || null, source: "catalog", model_id: modelId });
      toast.success(t("machines.addedFromCatalog", { name: data?.name || "" }));
      await load();
      setTab("custom");
      originalRef.current = data;
      setFormError("");
      setForm({ ...EMPTY, ...data });
      setEditing(data.id);
      setShowCustomSpecs(true);
    } catch (e) {
      toast.error((e.response && formatApiError(e.response.data?.detail)) || t("machines.saveError"));
    }
    setBusy(false);
  };

  const isInFleet = (modelId) => userMachines.some((m) => m.from_catalog_id === modelId);

  const brands = useMemo(() => ["", ...new Set(catalog.map((m) => m.brand))].sort(), [catalog]);
  const categoryOptions = useMemo(() => {
    const map = new Map();
    catalog.forEach((m) => {
      const c = map.get(m.category_id) || { id: m.category_id, name: m.category_name || m.category_id, n: 0 };
      c.n += 1;
      map.set(m.category_id, c);
    });
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [catalog]);
  const filtered = useMemo(() => {
    const tokens = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return catalog.filter((m) => {
      if (brandFilter && m.brand !== brandFilter) return false;
      if (categoryFilter && m.category_id !== categoryFilter) return false;
      const hay = `${m.brand} ${m.model} ${m.category_name || ""} ${m.year_range || ""} ${m.description || ""}`.toLowerCase();
      return tokens.every((tk) => hay.includes(tk));
    });
  }, [catalog, brandFilter, categoryFilter, search]);
  // Brands offered by the selected category only (no empty combinations).
  const brandOptions = useMemo(
    () => (categoryFilter ? [...new Set(catalog.filter((m) => m.category_id === categoryFilter).map((m) => m.brand))].sort() : brands.filter(Boolean)),
    [brands, catalog, categoryFilter]
  );
  useEffect(() => {
    if (brandFilter && !brandOptions.includes(brandFilter)) setBrandFilter("");
  }, [brandOptions, brandFilter]);
  const resetCatalogFilters = () => { setSearch(""); setCategoryFilter(""); setBrandFilter(""); };

  return (
    <AppLayout>
      <div className="px-8 py-8 max-w-7xl mx-auto" data-testid="machines-page-v2">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="label-tech mb-2">{t("nav.groupWorkshop")}</div>
            <h1 className="font-display font-bold text-3xl text-slate-100 flex items-center gap-3"><Wrench size={26} className="text-blue-400"/> {t("machines.title")}</h1>
          </div>
          {tab === "custom" && !editing && (
            <button onClick={startNew} className="btn-primary px-5 py-3 rounded-md flex items-center gap-2" data-testid="add-machine-btn">
              <Plus size={16}/> {t("machines.addMachine")}
            </button>
          )}
        </div>

        <div className="flex border-b border-slate-800 mb-6">
          <Tab active={tab==="custom"} onClick={() => setTab("custom")} icon={<Settings2 size={14}/>} label={`${t("machines.tabCustom")} (${userMachines.length})`} testid="tab-custom"/>
          <Tab active={tab==="catalog"} onClick={() => setTab("catalog")} icon={<BookOpen size={14}/>} label={`${t("machines.tabCatalog")} (${catalog.length})`} testid="tab-catalog"/>
        </div>

        {tab === "custom" && (
          <>
            {editing && (
              <form onSubmit={save} className="tech-card p-6 mb-6 fade-in-up" data-testid="machine-form">
                <div className="font-display font-semibold text-slate-100 mb-4">{editing==="new" ? t("machines.newMachine") : t("machines.editMachine")}</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Field label={t("machines.fieldName")} required value={form.name} onChange={(v) => setForm({...form, name: v})} testid="mf-name"/>
                  <Field label={t("machines.fieldBrand")} value={form.brand} onChange={(v) => setForm({...form, brand: v})}/>
                  <Field label={t("machines.fieldModel")} value={form.model} onChange={(v) => setForm({...form, model: v})}/>
                  <div>
                    <label className="label-tech block mb-2">{t("machines.category")} *</label>
                    <select required value={form.category_id} onChange={(e) => setForm({...form, category_id: e.target.value})} className="w-full px-4 py-3 rounded-md text-sm" data-testid="mf-category">
                      {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  <Field label={t("machines.hourlyRate")} type="number" value={form.hourly_rate} onChange={(v) => setForm({...form, hourly_rate: v})} testid="mf-rate"/>
                  <Field label={t("machines.precision")} type="number" value={form.precision_um} onChange={(v) => setForm({...form, precision_um: v})}/>
                  <Field label={t("machines.axes")} type="number" value={form.axes} onChange={(v) => setForm({...form, axes: v})}/>
                  <Field label={t("machines.workspace")} value={form.max_workspace_mm} onChange={(v) => setForm({...form, max_workspace_mm: v})}/>
                  <div className="md:col-span-3">
                    <label className="label-tech block mb-2">{t("machines.notes")}</label>
                    <textarea rows={2} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className="w-full px-4 py-3 rounded-md text-sm"/>
                  </div>
                </div>

                {/* Custom workshop specs (collapsible) */}
                <div className="mt-6 pt-5 border-t border-slate-800">
                  <button type="button" onClick={() => setShowCustomSpecs((s) => !s)}
                    className="w-full flex items-center justify-between text-left mb-3"
                    data-testid="toggle-custom-specs">
                    <div>
                      <div className="font-display font-semibold text-slate-100 flex items-center gap-2">
                        <Settings2 size={14} className="text-blue-400"/>
                        {t("machines.customSpecsTitle")}
                      </div>
                      <div className="text-xs text-slate-500 mt-1">{t("machines.customSpecsHint")}</div>
                    </div>
                    {showCustomSpecs ? <ChevronUp size={18} className="text-slate-400"/> : <ChevronDown size={18} className="text-slate-400"/>}
                  </button>

                  {showCustomSpecs && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2" data-testid="custom-specs-panel">
                      <Field label={t("machines.customRpmMax")} type="number" value={form.spindle_max_rpm} onChange={(v) => setForm({...form, spindle_max_rpm: v})} testid="cs-rpm"/>
                      <Field label={t("machines.customSpindleKw")} type="number" value={form.spindle_power_kw} onChange={(v) => setForm({...form, spindle_power_kw: v})} testid="cs-kw"/>
                      <Field label={t("machines.fieldController")} value={form.controller} onChange={(v) => setForm({...form, controller: v})} placeholder={t("machines.controllerPh")} testid="cs-controller"/>
                      <Field label={t("machines.customStrokeX")} type="number" value={form.travel_x_mm} onChange={(v) => setForm({...form, travel_x_mm: v})} testid="cs-x"/>
                      <Field label={t("machines.customStrokeY")} type="number" value={form.travel_y_mm} onChange={(v) => setForm({...form, travel_y_mm: v})} testid="cs-y"/>
                      <Field label={t("machines.customStrokeZ")} type="number" value={form.travel_z_mm} onChange={(v) => setForm({...form, travel_z_mm: v})} testid="cs-z"/>
                      <Field label={t("machines.fieldToolChanger")} type="number" value={form.tool_changer_capacity} onChange={(v) => setForm({...form, tool_changer_capacity: v})}/>
                      <Field label={t("machines.fieldMaxWorkpiece")} type="number" value={form.max_workpiece_kg} onChange={(v) => setForm({...form, max_workpiece_kg: v})}/>
                      <Field label={t("machines.fieldSpindleTaper")} value={form.spindle_taper} onChange={(v) => setForm({...form, spindle_taper: v})} placeholder={t("machines.taperPh")}/>
                      <Field label={t("machines.fieldCoolant")} value={form.coolant} onChange={(v) => setForm({...form, coolant: v})} placeholder="standard, high-pressure, MQL"/>
                      <Field label={t("machines.fieldYear")} type="number" value={form.year} onChange={(v) => setForm({...form, year: v})}/>
                      <Field label={t("machines.fieldLocation")} value={form.location_in_shop} onChange={(v) => setForm({...form, location_in_shop: v})}/>
                      <div className="md:col-span-3 flex flex-wrap items-center gap-6 pt-2">
                        <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                          <input type="checkbox" checked={!!form.has_probe} onChange={(e) => setForm({...form, has_probe: e.target.checked})} className="accent-blue-500"/>
                          {t("machines.hasProbe")}
                        </label>
                        <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                          <input type="checkbox" checked={!!form.has_pallet_changer} onChange={(e) => setForm({...form, has_pallet_changer: e.target.checked})} className="accent-blue-500"/>
                          {t("machines.hasPalletChanger")}
                        </label>
                      </div>
                      <div className="md:col-span-3">
                        <label className="label-tech block mb-2">{t("machines.customMaterials")}</label>
                        <input value={form.workable_materials} onChange={(e) => setForm({...form, workable_materials: e.target.value})} placeholder={t("machines.customMaterialsPlaceholder")} className="w-full px-4 py-3 rounded-md text-sm" data-testid="cs-materials"/>
                      </div>
                      <div className="md:col-span-3">
                        <label className="label-tech block mb-2">{t("machines.customCertifications")}</label>
                        <input value={form.certifications} onChange={(e) => setForm({...form, certifications: e.target.value})} placeholder={t("machines.customCertificationsPlaceholder")} className="w-full px-4 py-3 rounded-md text-sm" data-testid="cs-certs"/>
                      </div>
                      <div className="md:col-span-3">
                        <label className="label-tech block mb-2">{t("machines.customTechNotes")}</label>
                        <textarea rows={3} value={form.technical_notes} onChange={(e) => setForm({...form, technical_notes: e.target.value})} placeholder={t("machines.customTechNotesPlaceholder")} className="w-full px-4 py-3 rounded-md text-sm" data-testid="cs-tech-notes"/>
                      </div>
                    </div>
                  )}
                </div>

                {formError && (
                  <div className="mt-4 px-3 py-2 rounded-md bg-red-500/10 border border-red-500/40 text-sm text-red-300" data-testid="machine-form-error">{formError}</div>
                )}
                <div className="mt-5 flex gap-3">
                  <button type="submit" disabled={busy} data-testid="machine-form-save" className="btn-primary px-4 py-2 rounded-md flex items-center gap-2"><Save size={14}/> {busy ? t("common.saving") : t("common.save")}</button>
                  <button type="button" onClick={cancel} className="px-4 py-2 rounded-md text-sm bg-slate-800 text-slate-300 border border-slate-700 hover:border-slate-500"><X size={14}/> {t("common.cancel")}</button>
                </div>
              </form>
            )}

            {!editing && <QuickMachineSave categories={categories} onSave={quickSave} t={t}/>}

            {userMachines.length === 0 ? (
              <div className="tech-card p-16 text-center">
                <Wrench size={28} className="mx-auto text-slate-500 mb-3"/>
                <div className="font-display font-medium text-slate-100">{t("machines.emptyTitle")}</div>
                <div className="text-slate-500 text-sm mt-1">{t("machines.emptyHint")}</div>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {userMachines.map((m) => (
                  <div key={m.id} className="tech-card p-5" data-testid={`machine-row-${m.id}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="font-display font-semibold text-slate-100">{m.name}</div>
                        <div className="text-xs text-slate-500 mt-1">{m.brand} {m.model} · {m.axes ? `${m.axes} ${t("machines.axesShort")}` : ""} · ±{m.precision_um || "?"}µm</div>
                        {(m.spindle_max_rpm || m.spindle_power_kw || m.controller) && (
                          <div className="text-[10px] text-blue-300 mt-1 font-mono-data">
                            {m.spindle_max_rpm ? `${m.spindle_max_rpm} rpm` : ""}
                            {m.spindle_power_kw ? ` · ${m.spindle_power_kw}kW` : ""}
                            {m.controller ? ` · ${m.controller}` : ""}
                          </div>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="font-mono-data text-blue-400">€{m.hourly_rate}/h</div>
                        <div className="flex gap-1 mt-1">
                          <button onClick={() => startEdit(m)} className="p-1.5 rounded text-slate-400 hover:text-blue-400 hover:bg-blue-500/10" data-testid={`edit-machine-${m.id}`}><Edit3 size={14}/></button>
                          <button onClick={() => { setConfirmDelete(m.id); setDeleteError(""); }} className="p-1.5 rounded text-slate-400 hover:text-red-400 hover:bg-red-500/10" data-testid={`delete-machine-${m.id}`}><Trash2 size={14}/></button>
                        </div>
                      </div>
                    </div>
                    {confirmDelete === m.id && (
                      <div className="mb-3 p-3 rounded-md bg-red-500/10 border border-red-500/40" data-testid={`confirm-delete-${m.id}`}>
                        <div className="text-sm text-red-300 mb-2">{t("machines.deleteConfirm")} <strong>{m.name}</strong></div>
                        {deleteError && <div className="text-xs text-red-400 mb-2">{deleteError}</div>}
                        <div className="flex gap-2">
                          <button onClick={() => remove(m.id)} className="px-3 py-1.5 rounded text-xs bg-red-600 hover:bg-red-500 text-white flex items-center gap-1" data-testid={`confirm-delete-yes-${m.id}`}>
                            <Trash2 size={12}/> {t("common.yes")}, {t("common.delete").toLowerCase()}
                          </button>
                          <button onClick={() => { setConfirmDelete(null); setDeleteError(""); }} className="px-3 py-1.5 rounded text-xs bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700">
                            {t("common.cancel")}
                          </button>
                        </div>
                      </div>
                    )}
                    {m.notes && <div className="text-xs text-slate-400 mb-3">{m.notes}</div>}
                    {m.workable_materials && <div className="text-[11px] text-slate-300 mb-1"><span className="text-slate-500">{t("machines.customMaterials")}:</span> {m.workable_materials}</div>}
                    {m.certifications && <div className="text-[11px] text-slate-300 mb-1"><span className="text-slate-500">{t("machines.customCertifications")}:</span> {m.certifications}</div>}

                    {/* Workshop vs catalog optimal — radar comparison */}
                    {optimalByCategory.get(m.category_id) && (
                      <div className="mt-3">
                        <button
                          type="button"
                          onClick={() => toggleRadar(m.id)}
                          className="text-[11px] text-cyan-300 hover:text-cyan-200 flex items-center gap-1.5"
                          data-testid={`radar-toggle-${m.id}`}
                        >
                          <BarChart3 size={11}/>
                          {openRadar[m.id] ? "Nascondi" : "Mostra"} confronto specs vs categoria ottimale
                          {openRadar[m.id] ? <ChevronUp size={11}/> : <ChevronDown size={11}/>}
                        </button>
                        {openRadar[m.id] && (
                          <WorkshopVsCatalogRadar
                            userMachine={m}
                            catalogMachine={optimalByCategory.get(m.category_id)}
                          />
                        )}
                      </div>
                    )}

                    <div className="pt-3 border-t border-slate-800 mt-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="label-tech text-[10px]">{t("machines.documents")} ({(m.documents||[]).length})</div>
                        <label className={`flex items-center gap-1 text-xs ${uploads[m.id] != null ? "text-slate-500 cursor-wait" : "cursor-pointer text-blue-400 hover:text-blue-300"}`} title={t("machines.pdfHint")}>
                          {uploads[m.id] != null ? <Loader2 size={12} className="animate-spin"/> : <Upload size={12}/>} {t("machines.uploadPdf")}
                          <input type="file" accept="application/pdf" className="hidden" disabled={uploads[m.id] != null}
                            onChange={(e) => { uploadDoc(m.id, e.target.files[0]); e.target.value = ""; }} data-testid={`upload-doc-${m.id}`}/>
                        </label>
                      </div>
                      {uploads[m.id] != null && (
                        <div className="mb-2" data-testid={`upload-progress-${m.id}`}>
                          <div className="flex justify-between text-[11px] text-slate-400 mb-1"><span>{t("machines.pdfUploading")}</span><span className="font-mono-data">{uploads[m.id]}%</span></div>
                          <div className="h-1 rounded-full bg-slate-800 overflow-hidden"><div className="h-full bg-blue-500 transition-[width] duration-300" style={{ width: `${uploads[m.id]}%` }}/></div>
                        </div>
                      )}
                      {docErrors[m.id] && <div className="mb-2 text-[11px] text-red-400" data-testid={`upload-error-${m.id}`}>{docErrors[m.id]}</div>}
                      <div className="text-[10px] text-slate-500 mb-1">{t("machines.pdfHint")}</div>
                      {(m.documents||[]).map((d) => (
                        <div key={d.id} className="flex items-center justify-between text-xs text-slate-400 py-1">
                          <span className="flex items-center gap-1 truncate"><FileText size={11}/> {d.filename}</span>
                          <button onClick={() => removeDoc(m.id, d.id)} className="text-slate-500 hover:text-red-400"><Trash2 size={12}/></button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {tab === "catalog" && (
          <>
            <CatalogToolbar
              t={t} search={search} setSearch={setSearch}
              category={categoryFilter} setCategory={setCategoryFilter} categoryOptions={categoryOptions}
              brand={brandFilter} setBrand={setBrandFilter} brandOptions={brandOptions}
              count={filtered.length} total={catalog.length}
            />
            {filtered.length === 0 && (
              <div className="tech-card p-10 text-center text-sm text-slate-400" data-testid="catalog-empty">
                {t("machines.catalogEmpty")}
                <button type="button" onClick={resetCatalogFilters} className="block mx-auto mt-3 text-blue-400 hover:text-blue-300" data-testid="catalog-reset-filters">{t("dashboard.resetFilters")}</button>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {filtered.map((m) => {
                const owned = isInFleet(m.id);
                return (
                <div key={m.id} className="tech-card p-4 flex flex-col" data-testid={`catalog-${m.id}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="label-tech text-[10px] text-slate-500">{m.brand}</div>
                      <div className="font-display font-semibold text-slate-100">{m.model}</div>
                    </div>
                    <span className="font-mono-data text-blue-400 text-sm">€{m.hourly_rate}/h</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">{m.year_range} · {m.category_name}</div>
                  <div className="mt-3 grid grid-cols-2 gap-1 text-xs font-mono-data text-slate-400 flex-grow">
                    {m.axes && <div>{m.axes} {t("machines.axesShort")}</div>}
                    {m.precision_um && <div>±{m.precision_um}µm</div>}
                    {m.spindle_kw && <div>{m.spindle_kw} kW</div>}
                    {m.max_workspace_mm && <div>{m.max_workspace_mm}</div>}
                  </div>
                  <button onClick={() => !owned && addFromCatalog(m.id)} disabled={owned || busy}
                    className={`mt-3 w-full px-3 py-2 rounded-md text-xs font-medium flex items-center justify-center gap-2 transition-colors ${
                      owned ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/40 cursor-default" : "bg-blue-600 hover:bg-blue-500 text-white"
                    }`}
                    data-testid={`add-to-fleet-${m.id}`}>
                    {owned ? <><Check size={12}/> {t("machines.alreadyInFleet")}</> : <><PackagePlus size={12}/> {t("machines.addToFleet")}</>}
                  </button>
                </div>
              )})}
            </div>
          </>
        )}
      </div>
    </AppLayout>
  );
}

function Tab({ active, onClick, icon, label, testid }) {
  return (
    <button onClick={onClick} className={`px-4 py-2.5 text-sm flex items-center gap-2 border-b-2 ${active ? "border-blue-500 text-blue-400" : "border-transparent text-slate-400 hover:text-slate-200"}`} data-testid={testid}>
      {icon} {label}
    </button>
  );
}

function Field({ label, value, onChange, type = "text", required = false, placeholder = "", testid }) {
  return (
    <div>
      <label className="label-tech block mb-2">{label}</label>
      <input type={type} value={value ?? ""} required={required} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} className="w-full px-4 py-3 rounded-md text-sm" data-testid={testid}/>
    </div>
  );
}
